import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { type QueryClient } from '@tanstack/react-query';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';
import { useSaveFileMutation } from '#apps/client-web/features/file-storage/hooks/use-save-file-mutation';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { useDeleteFileMutation } from '#apps/client-web/features/file-storage/hooks/use-delete-file-mutation';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  AuditMechanicalFormSchema,
  type AuditMechanicalForm,
  type AuditMechanicalFormEntry,
  type RatingUnit,
} from '#packages/data-collection/models/audit-mechanical-form';
import { type Attachment } from '#packages/data-collection/models/common/attachment';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { useMenu } from '#packages/web/hooks/use-menu';

import { fromFormData, toFormData } from './mappers';
import { type EditAuditFormMechanicalEntryData } from './types';

export type AuditFormMechanicalEntryPageViewModel = ReturnType<
  typeof useAuditFormMechanicalEntryPageViewModel
>;

type AuditFormMechanicalEntryPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormMechanicalEntryPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormMechanicalEntryPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId, index: indexAsString } = useParams<{
    auditId: AuditId;
    index: string;
  }>();
  const index = parseInt(indexAsString!);

  const auditFormQuery = useLoadAuditFormByType<AuditMechanicalForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'mechanical',
    queryClient,
  });

  const saveFileStorageMutation = useSaveFileMutation({
    fileStorageService,
    queryClient,
  });

  const deleteFileStorageMutation = useDeleteFileMutation({
    fileStorageService,
    queryClient,
  });

  const fileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries[index]?.attachments ?? []).map(
        (item) => item.name,
      ),
    [auditFormQuery.data?.entries],
  );

  const fileStorageQuery = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditMechanicalForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'mechanical',
  });

  const form = useForm<EditAuditFormMechanicalEntryData>({
    reValidateMode: 'onChange',

    defaultValues: {},
  });

  const onSave = useCallback(
    (formData: EditAuditFormMechanicalEntryData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update mechanical entry, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating mechanical form data');

      const newFileIds = (formData.attachments ?? []).map(
        (attachment) => attachment.name,
      );
      const deletedFileIds = fileIds.filter(
        (fileId) => !newFileIds.includes(fileId),
      );
      deletedFileIds.forEach((fileId) => {
        deleteFileStorageMutation.mutate({
          fileId,
        });
      });

      const attachments: Attachment[] | undefined = formData.attachments?.map(
        (attachment) => {
          saveFileStorageMutation.mutate({
            fileId: attachment.name,
            content: attachment.file,
          });
          return {
            name: attachment.name,
            mimeType: attachment.file.type,
            isSelectedForReport: attachment.isSelectedForReport,
          };
        },
      );

      // Encode existing data to raw format, or start with empty entries
      const previousDataEncoded = auditFormQuery.data
        ? AuditMechanicalFormSchema.encode(auditFormQuery.data)
        : { type: 'mechanical' as const, entries: [] };

      const newEntryRaw = fromFormData(formData, attachments);

      if (index === -1) {
        previousDataEncoded.entries.push(newEntryRaw);
      } else {
        previousDataEncoded.entries[index] = newEntryRaw;
      }

      logger?.debug({ entry: previousDataEncoded.entries[index] });

      // Decode once to validate and transform
      const auditMechanicalForm =
        AuditMechanicalFormSchema.decode(previousDataEncoded);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditMechanicalForm },
        {
          onSuccess: (savedForm: AuditMechanicalForm) => {
            logger?.debug(
              { auditId, savedForm },
              'Mechanical form updated successfully',
            );
            // invalidate audit mechanical form queries
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('Mechanical form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update mechanical form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      index,
      auditId,
      saveFileStorageMutation.mutate,
      auditFormQuery.data,
      AuditMechanicalFormSchema.encode,
      AuditMechanicalFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (
      (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) &&
      (saveFileStorageMutation.isIdle || saveFileStorageMutation.isSuccess) &&
      (deleteFileStorageMutation.isIdle || deleteFileStorageMutation.isSuccess)
    ) {
      return { state: 'idle' };
    }

    if (
      saveAuditFormMutation.isPending ||
      saveFileStorageMutation.isPending ||
      deleteFileStorageMutation.isPending
    ) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    if (saveFileStorageMutation.isError) {
      return { state: 'error', error: saveFileStorageMutation.error };
    }

    if (deleteFileStorageMutation.isError) {
      return { state: 'error', error: deleteFileStorageMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
    saveFileStorageMutation.isIdle,
    saveFileStorageMutation.isPending,
    saveFileStorageMutation.isSuccess,
    saveFileStorageMutation.isError,
    saveFileStorageMutation.error,
    deleteFileStorageMutation.isIdle,
    deleteFileStorageMutation.isPending,
    deleteFileStorageMutation.isSuccess,
    deleteFileStorageMutation.isError,
    deleteFileStorageMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{
      form: AuditMechanicalFormEntry | undefined;
      files: (FileContent | undefined)[];
    }>
  >(() => {
    const areFilesLoaded =
      fileIds.length === 0 ||
      fileStorageQuery.every((query) => query.isSuccess);
    if (auditFormQuery.isSuccess && areFilesLoaded) {
      const files =
        fileStorageQuery.map((item) => item.data ?? undefined) ?? undefined;
      return {
        state: 'success',
        data: { form: auditFormQuery.data?.entries[index], files },
      };
    }

    const areFilesLoading =
      fileIds.length > 0 &&
      fileStorageQuery.some((query) => query.isLoading || query.isPending);
    if (
      auditFormQuery.isLoading ||
      auditFormQuery.isPending ||
      areFilesLoading
    ) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return {
        state: 'error',
        error: auditFormQuery.error,
      };
    }

    const fileError = fileStorageQuery.find((query) => query.isError)?.error;
    if (!isNil(fileError)) {
      return {
        state: 'error',
        error: fileError,
      };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    fileStorageQuery,
    fileIds,
    index,
  ]);

  const mainMenu = useMenu();

  const deleteDialog = useDialog<AuditMechanicalFormEntry>();
  const isDeleteDisabled =
    updateMutationState.state === 'pending' ||
    loadAuditFormState.state !== 'success' ||
    loadAuditFormState.data.form == null ||
    index < 0;
  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (isDeleteDisabled || isNil(auditFormQuery.data)) {
        logger?.debug(
          'Cannot delete mechanical entry, page is not in success state for an existing entry',
        );
        return;
      }

      mainMenu.close();

      // show confirmation dialog
      if (!confirmed) {
        logger?.debug('Opening mechanical entry deletion confirmation dialog');
        deleteDialog.open(loadAuditFormState.data.form);
        return;
      }

      const updatedData = { ...auditFormQuery.data };
      updatedData.entries.splice(index, 1);

      // perform deletion
      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: updatedData },
        {
          onSuccess: (auditForm) => {
            logger?.debug(
              { auditId, auditForm },
              'Mechanical entry deleted successfully',
            );

            // invalidate audit list queries
            queryClient
              .invalidateQueries({
                queryKey: [AUDIT_FORMS_QUERY_KEY, auditId, updatedData.type],
              })
              .then(() => {
                logger?.debug('Mechanical list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate mechanical list queries',
                );
                throw err;
              });
          },

          onError: (err) => {
            logger?.error(
              { err, auditId },
              'Failed to delete mechanical entry',
            );

            throw err;
          },
        },
      );
    },
    [auditId, loadAuditFormState, saveAuditFormMutation],
  );

  const changeRatingUnit = useCallback(
    (ratingUnit: RatingUnit | undefined) => {
      form.setValue('ratingUnit', ratingUnit);

      switch (ratingUnit) {
        case 'hp':
        case 'kw':
        case 'tons_cooling': {
          form.setValue('fuelType', 'electricity');
          break;
        }
        case 'btu_per_hour':
        case 'gph_gallon_per_hour': {
          form.setValue('fuelType', 'natural_gas');
          break;
        }
        default: {
          form.setValue('fuelType', undefined);
          break;
        }
      }
    },
    [form.setValue],
  );

  // sync form default values when audit details are loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditMechanicalForm: auditFormQuery.data },
      'Audit mechanical form default values',
    );

    // update form default values
    form.reset(
      toFormData(loadAuditFormState.data.form, loadAuditFormState.data.files),
    );
  }, [auditFormQuery.data?.entries, auditFormQuery.isSuccess]);

  return {
    mainMenu,

    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    onDelete,
    deleteDialog,
    isDeleteDisabled,

    changeRatingUnit,

    auditId,
  };
};
